export const STAGES = ["Tracking", "Sourcing", "Unsolicited Offer", "Screening", "Underwriting", "LOI Submitted", "In Diligence", "Acquired", "Passed", "Lost", "Dead"];

// Priority order for pipeline sorting (lower = shown first).
// "Unsolicited Offer" sits between active outreach and a formal LOI — it's
// the stage assigned automatically by Sourcing when a sourced LOI is fired
// before a deal record exists.
// "Lost" is for deals where we actively competed (submitted LOI/offer) but
// were beaten — sits between Passed (we declined) and Dead (the opportunity
// is closed / no longer viable).
// "Acquired" is a closed deal: further along than In Diligence (so funnel
// ranks count it as having got there) but not part of the active pipeline.
export const STAGE_ORDER = {
  "Acquired":    -1,
  "In Diligence": 0,
  "LOI Submitted":    1,
  "Unsolicited Offer": 2,
  "Underwriting": 3,
  "Screening":    4,
  "Sourcing":     5,
  "Tracking":     6,
  "Passed":       7,
  "Lost":         8,
  "Dead":         9,
};

// Terminal stages — moving to any of these opens the Kill modal in App.js so
// we capture killed_reason + killed_narrative (and, for Lost, competition
// detail). Moving OFF a terminal stage clears killed_at.
export const KILLED_STAGES = ["Passed", "Lost", "Dead"];

// Reason enum shown in the Kill modal. Different reasons apply to different
// stages so the dropdown is filtered by which terminal stage the deal is
// moving into. Values are stored verbatim in deals.killed_reason.
export const KILL_REASONS = {
  Passed: [
    "Price too high",
    "Poor thesis fit",
    "Off-market / off-strategy",
    "Bad physical (age, height, layout)",
    "Bad tenant / rollover risk",
    "Bad location (submarket)",
    "Timing (no capital available)",
    "Passed for a better deal",
    "Other",
  ],
  Lost: [
    "Lost to competitor — outbid",
    "Lost to competitor — better structure",
    "Lost to competitor — faster close",
    "Lost — broker steered elsewhere",
    "Withdrew during best-and-final",
    "Withdrew during diligence",
    "Other",
  ],
  Dead: [
    "Seller pulled deal",
    "Seller unresponsive",
    "Deal never launched",
    "Not the right seller / not for sale",
    "Broker relationship soured",
    "Other",
  ],
};

export const STAGE_COLORS = {
  Tracking:            "bg-[rgba(138,138,138,0.14)] text-[#8a8a8a] border border-[rgba(138,138,138,0.25)]",
  Sourcing:            "bg-[rgba(138,138,138,0.14)] text-[#8a8a8a] border border-[rgba(138,138,138,0.25)]",
  // Retired name of Sourcing; a row not yet renamed still gets a colour.
  Outreach:            "bg-[rgba(138,138,138,0.14)] text-[#8a8a8a] border border-[rgba(138,138,138,0.25)]",
  // The Outreach section (broker LOIs) - its own flow and stages.
  "LOI Sent":          "bg-[rgba(155,107,204,0.16)] text-[#b892e0] border border-[rgba(155,107,204,0.32)]",
  "Confirmed Receipt": "bg-[rgba(200,146,47,0.16)] text-[#c8922f] border border-[rgba(200,146,47,0.32)]",
  Countered:           "bg-[rgba(233,53,41,0.16)] text-[#f04f44] border border-[rgba(233,53,41,0.34)]",
  Declined:            "bg-[rgba(36,36,36,0.9)] text-[#7a7a7a] border border-[rgba(106,106,106,0.22)]",
  "Unsolicited Offer": "bg-[rgba(138,138,138,0.14)] text-[#8a8a8a] border border-[rgba(138,138,138,0.25)]",
  Screening:           "bg-[rgba(138,138,138,0.22)] text-[#b4b4b4] border border-[rgba(138,138,138,0.35)]",
  Underwriting:        "bg-[rgba(200,146,47,0.16)] text-[#c8922f] border border-[rgba(200,146,47,0.32)]",
  "LOI Submitted":     "bg-[rgba(233,53,41,0.16)] text-[#f04f44] border border-[rgba(233,53,41,0.34)]",
  "In Diligence":      "bg-[rgba(79,157,118,0.16)] text-[#7cbb99] border border-[rgba(79,157,118,0.32)]",
  Acquired:            "bg-[rgba(79,157,118,0.30)] text-[#a9dcc0] border border-[rgba(79,157,118,0.50)]",
  Passed:              "bg-[rgba(138,138,138,0.12)] text-[#8a8a8a] border border-[rgba(138,138,138,0.22)]",
  Lost:                "bg-[rgba(233,53,41,0.10)] text-[#e93529] border border-[rgba(233,53,41,0.22)]",
  Dead:                "bg-[rgba(36,36,36,0.9)] text-[#7a7a7a] border border-[rgba(106,106,106,0.22)]",
};

// ── Sourcing ────────────────────────────────────────────────────────────────
// Owner LOIs fired from Sourcing's LOI Sender. The stage was called
// "Outreach" (and before that "Unsolicited Offer"); both old names still
// count as Sourcing so a row not yet renamed behaves the same.
export const SOURCING_STAGES = ["Sourcing", "Outreach", "Unsolicited Offer"];

// ── Outreach section ────────────────────────────────────────────────────────
// LOIs submitted through a broker (deals.deal_flow = 'outreach'). Their own
// stages, separate from Pipeline's:
//     LOI Sent -> Confirmed Receipt -> Countered -> moves into Pipeline
//     LOI Sent -> Confirmed Receipt -> Declined
// Moving forward takes the deal into Pipeline at OUTREACH_PROMOTE_STAGE.
export const OUTREACH_STAGES = ["LOI Sent", "Confirmed Receipt", "Countered", "Declined"];
export const OUTREACH_CLOSED_STAGES = ["Declined"];
export const OUTREACH_PROMOTE_STAGE = "Underwriting";
export const OUTREACH_STAGE_ORDER = { "Countered": 0, "Confirmed Receipt": 1, "LOI Sent": 2, "Declined": 3 };
export const isOutreachDeal = (d) => (d?.dealFlow || d?.deal_flow) === "outreach";
